// Cosmetic award history survives table component remounts and full page
// reloads on rejoin. sessionStorage is tab-scoped, so another browser session
// is independent while this tab cannot replay a completed server snapshot.
const MAX_AWARDS = 2048;
const STORAGE_KEY = 'hijack.win-chip-awards.v1';

const awardStorage = (): Storage | undefined => {
  try {
    return typeof window === 'undefined' ? undefined : window.sessionStorage;
  } catch {
    return undefined;
  }
};

const readPlayedAwards = (): string[] => {
  try {
    const value = awardStorage()?.getItem(STORAGE_KEY);
    const parsed = value ? JSON.parse(value) : [];
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
};

const playedAwards = new Set<string>(readPlayedAwards().slice(-MAX_AWARDS));
// Continuations are memory-only and expire at the original flight's end.
// The persistent played set still prevents an old snapshot replay after reload.
const interruptedFlights = new Map<string, number>();
const FLIGHT_MS = 1120;

export const interruptWinChipFlight = (id: string, deadline: number): void => {
  if (deadline <= Date.now()) return;
  interruptedFlights.set(id, deadline);
  if (interruptedFlights.size > MAX_AWARDS) {
    const oldest = interruptedFlights.keys().next().value;
    if (oldest !== undefined) interruptedFlights.delete(oldest);
  }
};

export const resumeWinChipFlight = (id: string): { deadline: number; progress: number } | null => {
  const deadline = interruptedFlights.get(id);
  interruptedFlights.delete(id);
  if (deadline === undefined || deadline <= Date.now()) return null;
  return { deadline, progress: Math.max(0, Math.min(1, 1 - (deadline - Date.now()) / FLIGHT_MS)) };
};

export const discardWinChipContinuation = (id: string): void => {
  interruptedFlights.delete(id);
};

export const newWinChipFlightDeadline = (): number => Date.now() + FLIGHT_MS;

const persistPlayedAwards = () => {
  try {
    awardStorage()?.setItem(STORAGE_KEY, JSON.stringify(Array.from(playedAwards)));
  } catch {
    // Storage can be unavailable in privacy-restricted browsers. The in-memory
    // registry still protects ordinary component remounts in that case.
  }
};

export const winChipAwardId = (
  table: string,
  gameNo: unknown,
  pot: number,
  player: unknown,
  handType: string,
  runs: unknown = ''
): string | undefined => {
  if (!table || gameNo === null || gameNo === undefined || String(gameNo) === '') return undefined;
  // Engine encodes active multi-run as totalRuns,startPhase,currentRun.
  const parts = String(runs).split(',');
  const run = parts.length === 3 ? Number(parts[2]) : 1;
  return JSON.stringify([table, String(gameNo), run, Number(pot), String(player), handType || '']);
};

export const claimWinChipAward = (id: string): boolean => {
  if (playedAwards.has(id)) return false;
  playedAwards.add(id);
  if (playedAwards.size > MAX_AWARDS) {
    const oldest = playedAwards.values().next().value;
    if (oldest !== undefined) playedAwards.delete(oldest);
  }
  persistPlayedAwards();
  return true;
};

export const __resetWinChipAwards = (): void => {
  playedAwards.clear();
  interruptedFlights.clear();
  try {
    awardStorage()?.removeItem(STORAGE_KEY);
  } catch {
    // Test/reset helper mirrors the production storage fallback above.
  }
};
